import { SlideDataServices } from '@src/slide/domain/slide-app/core/slide-data-services.core';
import { ErrCodeListener } from './err-code-listener';
import CommonTips, { CommonTipsActionType, CommonTipsType, ICommonTipsConfig } from '@com/common-tips';
import i18n from '@i18next/i18n';
import { network } from '@tencent/netcheck';
import { OnlineEditServices } from '@src/slide/domain/slide-app/scenes/online/core/online-edit-services.core';
import { dbBrokenText, mutexLockText } from './constants';
import ArkLoader from '@src/js/common/ark-extensions';
import React from 'react';
import Tooltip from '@tencent/dui/lib/components/Tooltip';
import { browser } from '@src/utils/agent';
import { tencentDocOpenUrlReload } from '@tencent/tencent_doc_open_url';
import { IServerMessage, IServerMessageBody } from '@tencent/docs-connection';

const editOfflineText = i18n.t('正在离线编辑，编辑内容已缓存，联网后将自动同步云端');
const viewOfflineText = i18n.t('离线查看中，文档最新内容会在网络恢复后同步。');
const offlineTipsId = 'offline-tips';
const syncTipsId = 'sync-tips';

export interface ISocketListener {
    closeOfflineTips: () => void;
    closeSyncTips: () => void;
    handleOnOnline: () => void;
    handleOnOffline: () => void;
}

export default class SocketListener extends ErrCodeListener implements ISocketListener {
    private isShowOfflineTips = false;
    private isShowSyncTips = false;

    public constructor(
        shouldListenSocketChange: boolean,
        shouldListenSocketCommitUpload?: boolean,
    ) {
        super();
        SlideDataServices.SDKDataServices.EventManager.promise().then(({ socketEvents }) => {
            // 非本地编辑&非预览态 才增加netChange监听
            if (shouldListenSocketChange) {
                let unRegisterShutDown = () => {};
                unRegisterShutDown = socketEvents.onShutDown(this.handleSlideAppErrorCode);
                const unRegisterOff = socketEvents.onChangeToOffline(this.handleOnOffline);
                const unRegisterOnline = socketEvents.onChangeToOnline(this.handleOnOnline);
                window.addEventListener('beforeunload', () => {
                    unRegisterShutDown();
                    unRegisterOff();
                    unRegisterOnline();
                });
                this.netChangeEvent();
            }
            if (shouldListenSocketCommitUpload) {
                socketEvents.onCommitUpload(this.handleCommitUpload);
            }
        });
    }

    public closeOfflineTips = () => {
        this.isShowOfflineTips = false;
        CommonTips.getInstance().hideById(offlineTipsId, true);
    };

    public closeSyncTips = () => {
        this.isShowSyncTips = false;
        CommonTips.getInstance().hideById(syncTipsId);
    };

    public handleOnOnline = () => {
        if (!this.isShowOfflineTips || this.isShowSyncTips) return;
        const commonTipsConfig = {
            message: i18n.t('离线数据同步中，请稍候'),
            commonTipsType: CommonTipsType.INFO,
            autoClose: true,
            block: false,
            closeCallback: this.closeSyncTips,
        };
        this.closeOfflineTips();
        if (!window.pray().isPlaying) {
            CommonTips.getInstance().show(commonTipsConfig);
        } else {
            CommonTips.getInstance().clearStorage();
        }
    };

    public handleOnOffline = () => {
        if (this.isShowOfflineTips) {
            return;
        }
        const commonTipsConfig = Object.assign({}, {
            message: '',
            commonTipsType: CommonTipsType.INFO,
            block: false,
            id: offlineTipsId,
        }, this.getOfflineTipsConfig());
        this.changeSaveTipsOfflineText(commonTipsConfig.message);
        if (!window.pray().isPlaying) {
            CommonTips.getInstance().show(commonTipsConfig);
        } else {
            CommonTips.getInstance().storage(commonTipsConfig);
        }
        this.isShowOfflineTips = true;
    };

    private netChangeEvent() {
        let delayHandleOnOffline: NodeJS.Timeout;
        network.changeEnd((status: string) => {
            const socket = OnlineEditServices.CollaborationManager()?.getSocket();
            if (status === 'online') {
                clearTimeout(delayHandleOnOffline);
                // 如果都没有socket，就直接相信网络状态，变在线
                if (!socket) {
                    this.handleOnOnline();
                    return;
                }
                // 如果有socket，就看socket的状态，必须是在线后才能变在线
                if (socket.opened) {
                    this.handleOnOnline();
                    return;
                }
                return;
            }
            delayHandleOnOffline = setTimeout(() => {
                this.handleOnOffline();
            });
        });
    }

    private getOfflineTipsConfig(): ICommonTipsConfig {
        const editable = SlideDataServices.PrivilegeManager()?.editable;
        const persistent = OnlineEditServices.CollaborationManager()?.getPersistent();
        const basicConfig = { closeCallback: () => this.isShowOfflineTips = false };
        if (!editable) return { message: viewOfflineText, block: false, ...basicConfig };
        if (persistent?.mutexLockHelper
            .isLockedByOtherPage()) return { message: mutexLockText, block: true, ...basicConfig };
        if (!persistent?.supportOfflineEdit) {
            return {
                message: dbBrokenText,
                block: true,
                customerActions: [{ text: this.getDBErrorToolTip() }],
                shouldCloseAfterAction: false,
                ...basicConfig,
            };
        }
        return { message: editOfflineText, block: false, closable: true, ...basicConfig };
    }

    private changeSaveTipsOfflineText = async (text: string) => {
        const component: any = await ArkLoader.extensionService.getExtension('docs-component-save-tips');
        component.store.dispatch.saveTips.setOfflineText(text);
    };

    private getDBErrorToolTip() {
        return React.createElement(Tooltip, {
            title: i18n.t('本地缓存文件写入失败，暂时无法离线缓存您的数据，请保持网络连接以确保数据实时更新。您可以尝试清理本地磁盘空间或重启客户端。'),
            trigger: browser.isMobile ? 'click' : 'hover',
        }, React.createElement('span', {}, i18n.t('详情')));
    }

    private handleCommitUpload = async (data?: IServerMessage) => {
        const body = (data?.body || {}) as IServerMessageBody;
        const tinyid = SlideDataServices.DocInfoManager()?.tinyid;
        // 如果操作用户是自己, 则屏蔽弹窗
        if (body?.data?.author === tinyid) {
            tencentDocOpenUrlReload();
            return;
        }
        const collaborationManager = await OnlineEditServices.CollaborationManager.promise();
        const commonTipsConfig: ICommonTipsConfig = {
            message: i18n.t('有人上传了新版本，请刷新查看最新内容，如需恢复可前往修订记录处理。'),
            commonTipsType: CommonTipsType.INFO,
            onlyShowOne: true,
            block: true,
            actionType: CommonTipsActionType.REFRESH,
            callBack: () => {},
        };
        if (collaborationManager.getPersistent().getMutationsPacks().length) {
            commonTipsConfig.actionType = CommonTipsActionType.REFRESH
            | CommonTipsActionType.EXPORT_SNAP_SHOT | CommonTipsActionType.CREATE_COPY_DOC;
            commonTipsConfig.callBack = (actionType: number, fn?: any) => {
                if (actionType === CommonTipsActionType.CREATE_COPY_DOC) {
                    fn(true);
                }
            };
        }
        CommonTips.getInstance().show(commonTipsConfig);
    };
}
